import React, { memo, ChangeEvent } from 'react'
import { Select, ISelectOption } from 'components/forms/Selects'
import { Button } from 'transfix-ui/components/Button'
import { IconButton } from 'transfix-ui/components/IconButton'
import SuggestLocation, { ILocationSuggestion } from 'transfix-ui/components/SuggestLocation'
import { ICarrierCapacity } from 'apps/marketplace/store/contractLanes/types'
import { RADIUS_OPTIONS } from 'apps/marketplace/utils/constants/radii'
import { TextInput } from 'components/atoms'
import { DeleteIcon } from 'transfix-icons'
import { capitalize } from 'utils/capitalize'
import { FORMATS } from 'constants/datetime'
import { DAY_LIMITS, dayLimitsKey } from './CapacityDialog'

const convertToLocationForm = (capacity: ICarrierCapacity) => {
  const { pickupLocation } = capacity || {}
  const { city, stateCode: state } = pickupLocation || {}
  return { state, city }
}

const convertRadiusValue = (value: number) =>
  RADIUS_OPTIONS.find(radiusMile => radiusMile.value === value)

const dayOfWeekList = FORMATS.fullWeekList.map(dayOfWeek => capitalize(dayOfWeek.substr(0, 3)))

interface CapacityEntryFormProps {
  capacity: ICarrierCapacity
  updateLocation: (e: ILocationSuggestion) => void
  updateRadius: (option: ISelectOption<number>) => void
  updateDayLimit: (e: ChangeEvent<HTMLInputElement>) => void
  addCapacity?: () => void
  deleteCapacity?: () => void
}

export const CapacityEntryForm = memo<CapacityEntryFormProps>(
  ({ addCapacity, capacity, deleteCapacity, updateDayLimit, updateLocation, updateRadius }) => {
    const capacityUuid = capacity.uuid
    const locationId = capacityUuid ? `${capacityUuid}:pickup` : 'new-location-pickup'
    const radiusId = capacityUuid ? `${capacityUuid}:radius` : 'new-radius-pickup-within'
    const radiusValue = capacity.pickupLocation?.radius
    return (
      <>
        <div className='row'>
          <div className='col-xs-5'>
            <SuggestLocation
              label='Pickup Point'
              placeholder='Pickup City'
              value={convertToLocationForm(capacity)}
              onChange={updateLocation}
              resultTypes={['City', 'PostalCode']}
              errorMessage={capacity.pickupLocationError || ''}
              dataTest='location-pickup'
              id={locationId}
            />
          </div>
          <div className='col-xs-5'>
            <Select
              label='Pick up within (mi)'
              options={RADIUS_OPTIONS}
              value={radiusValue && convertRadiusValue(radiusValue)}
              onChange={updateRadius}
              dataTest='radius-pickup-within'
              id={radiusId}
            />
          </div>
          {capacity.uuid && (
            <div className='col-xs-2'>
              <div className='fr'>
                <IconButton
                  Icon={DeleteIcon}
                  onClick={deleteCapacity}
                  color='tertiary'
                  data-test='delete-capacity'
                  label='Edit'
                  size='large'
                />
              </div>
            </div>
          )}
        </div>
        <div className='row'>
          {dayOfWeekList.map(day => {
            const theDay = day as dayLimitsKey
            const dayId = capacity.uuid ? `${capacity.uuid}:${theDay}` : theDay
            const dayLimit = DAY_LIMITS[theDay]
            return (
              <div className='col-xs-1' key={dayId}>
                <TextInput
                  dataTest={`day-input-${theDay}`}
                  id={dayId}
                  placeholder='0'
                  label={theDay}
                  value={capacity[dayLimit]}
                  onChange={updateDayLimit}
                  invalid={false}
                  clearable={false}
                />
              </div>
            )
          })}
        </div>
        {!capacity.uuid && (
          <Button color='primary' onClick={addCapacity} data-test='add-reference'>
            Add Pickup Point
          </Button>
        )}
      </>
    )
  }
)
